/**
 * ClimateScenarios — NGFS climate scenario analysis + Climate VaR (ESRS E1 /
 * ISSB S2 / TCFD quantitative disclosure). Backend:
 * routes/climate_scenario_routes.py (prefix /api/climate/scenarios) over
 * services/climate_scenario_service.py. Dark-theme chrome from EsgKit; every
 * data value in JetBrains Mono; status is always colour + icon + label.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Thermometer, Building2, Play, Waves, Coins, CheckCircle2,
  AlertTriangle, Flame, Plus, ChevronDown, ShieldAlert,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, TabBar,
  DataTable, EmptyState, ErrorBanner, Skeleton, field, Label, DataQualityBadge,
  apiFetch, fmt, C, MONO, SANS, HEAD,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";

// ── Static reference ─────────────────────────────────────────────────────────
// The six NGFS ids the backend enum accepts (ClimateScenario). Used for the
// Run Analysis / VaR checkbox chips; the reference strip itself is driven by
// the live GET scenarios list.
const NGFS_IDS = [
  "orderly_1.5c", "orderly_2c", "disorderly_1.5c",
  "disorderly_2c", "hot_house_3c", "hot_house_4c",
];

const ASSET_TYPES = ["property", "equipment", "inventory"];
const FLOOD_ZONES = ["none", "low", "medium", "high"];
const WATER_STRESS = ["low", "medium", "high", "extremely_high"];

// Scenario family → accent tone + status icon (orderly / disorderly / hot_house).
function scenarioTone(id = "") {
  if (id.startsWith("orderly"))
    return { color: C.primary, Icon: CheckCircle2, family: "Orderly" };
  if (id.startsWith("disorderly"))
    return { color: C.accent, Icon: AlertTriangle, family: "Disorderly" };
  if (id.startsWith("hot_house"))
    return { color: C.destructive, Icon: Flame, family: "Hot house" };
  return { color: C.neutral, Icon: AlertTriangle, family: "Scenario" };
}

const zoneTone = (z) =>
  z === "high" || z === "extremely_high" ? C.destructive
    : z === "medium" ? C.accent
      : z === "low" ? C.primary
        : C.neutral;

const USD = (n) => (n === null || n === undefined || n === "" ? "—" : `$${fmt(n, 0)}`);

// Severity scale: interpolate a loss figure toward destructive-red by its
// share of the largest loss in the set (auditors also see the mono figure).
function severityColor(v, max) {
  if (!max || !v || v <= 0) return C.body;
  const t = Math.min(1, v / max);
  return t > 0.66 ? C.destructive : t > 0.33 ? C.accent : C.primary;
}

// ── Small building blocks ────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);

const Input = (props) => <input {...props} style={{ ...field, ...(props.style || {}) }} />;
const Select = ({ children, ...p }) => (
  <select {...p} style={{ ...field, ...(p.style || {}) }}>{children}</select>
);

const grid = (min = 200) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
  gap: 12,
});

// Scenario checkbox chip (Run Analysis / VaR selectors).
const ScenarioChip = ({ id, selected, onToggle }) => {
  const { color, Icon, family } = scenarioTone(id);
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      title={family}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px",
        borderRadius: 999, cursor: "pointer", fontFamily: MONO, fontSize: 11,
        fontWeight: 600, whiteSpace: "nowrap",
        color: selected ? color : C.body,
        background: selected ? `${color}1A` : "transparent",
        border: `1px solid ${selected ? `${color}66` : C.border}`,
        transition: "background .15s, border-color .15s, color .15s",
      }}
    >
      <Icon size={12} strokeWidth={1.5} /> {id}
    </button>
  );
};

const SectionTitle = ({ Icon, children, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
    <Overline Icon={Icon} style={{ marginBottom: 0 }}>{children}</Overline>
    {right}
  </div>
);

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ClimateScenarios() {
  const { entities, entityId, setEntityId } = useEntities("climate_scenarios_entity_id");

  const [tab, setTab] = useState("assets");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [scenarios, setScenarios] = useState([]);   // GET "" catalog
  const [provenance, setProvenance] = useState(null); // catalog data-quality
  const [assets, setAssets] = useState([]);          // GET /assets

  // Result panels held in state.
  const [runResult, setRunResult] = useState(null);
  const [varResult, setVarResult] = useState(null);
  const [sensResult, setSensResult] = useState(null);

  // ── load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const [cat, ass] = await Promise.all([
        apiFetch(`/api/climate/scenarios`),
        apiFetch(`/api/climate/scenarios/assets?entity_id=${encodeURIComponent(entityId)}`),
      ]);
      setScenarios(cat?.scenarios || []);
      setProvenance(cat?.provenance || null);
      setAssets(ass?.assets || []);
    } catch (e) {
      setError(e.message || "Failed to load climate data");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  // ── Asset registration ───────────────────────────────────────────────────────
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [savingAsset, setSavingAsset] = useState(false);
  const emptyAsset = {
    asset_name: "", asset_type: "property", location: "", country: "",
    latitude: "", longitude: "", value_usd: "", useful_life_years: "20",
    coastal_proximity_km: "", flood_zone: "none", water_basin_stress: "low",
  };
  const [assetForm, setAssetForm] = useState(emptyAsset);
  const setAF = (k, v) => setAssetForm((f) => ({ ...f, [k]: v }));

  const submitAsset = async (e) => {
    e.preventDefault();
    setSavingAsset(true);
    setError("");
    try {
      const body = {
        entity_id: entityId,
        asset_name: assetForm.asset_name,
        asset_type: assetForm.asset_type,
        location: assetForm.location,
        country: assetForm.country.toUpperCase(),
        latitude: Number(assetForm.latitude),
        longitude: Number(assetForm.longitude),
        value_usd: Number(assetForm.value_usd),
        useful_life_years: Number(assetForm.useful_life_years) || 20,
        flood_zone: assetForm.flood_zone,
        water_basin_stress: assetForm.water_basin_stress,
      };
      if (assetForm.coastal_proximity_km !== "")
        body.coastal_proximity_km = Number(assetForm.coastal_proximity_km);
      await apiFetch(`/api/climate/scenarios/assets`, { method: "POST", body });
      setAssetForm(emptyAsset);
      setShowAssetForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to register asset");
    } finally {
      setSavingAsset(false);
    }
  };

  // ── Run Analysis form ────────────────────────────────────────────────────────
  const [runSel, setRunSel] = useState([...NGFS_IDS]);
  const [runForm, setRunForm] = useState({
    reporting_period: "FY2026",
    scope1_emissions: "", scope2_emissions: "", scope3_emissions: "",
    fossil_fuel_assets_value: "", sector: "",
  });
  const setRF = (k, v) => setRunForm((f) => ({ ...f, [k]: v }));
  const [running, setRunning] = useState(false);
  const toggleRun = (id) =>
    setRunSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const transitionExposure = (f) => ({
    scope1_emissions: Number(f.scope1_emissions) || 0,
    scope2_emissions: Number(f.scope2_emissions) || 0,
    scope3_emissions: Number(f.scope3_emissions) || 0,
    fossil_fuel_assets_value: Number(f.fossil_fuel_assets_value) || 0,
    sector: f.sector || "",
  });

  const runAnalysis = async (e) => {
    e.preventDefault();
    setRunning(true);
    setError("");
    try {
      const body = {
        entity_id: entityId,
        reporting_period: runForm.reporting_period,
        scenarios: runSel,
        transition_exposure: transitionExposure(runForm),
      };
      const r = await apiFetch(`/api/climate/scenarios/run`, { method: "POST", body });
      setRunResult(r);
    } catch (err) {
      setError(err.message || "Scenario run failed");
    } finally {
      setRunning(false);
    }
  };

  // ── Climate VaR form ─────────────────────────────────────────────────────────
  const [varSel, setVarSel] = useState([...NGFS_IDS]);
  const [varForm, setVarForm] = useState({
    scope1_emissions: "", scope2_emissions: "", scope3_emissions: "",
    fossil_fuel_assets_value: "", sector: "",
  });
  const setVF = (k, v) => setVarForm((f) => ({ ...f, [k]: v }));
  const [runningVar, setRunningVar] = useState(false);
  const toggleVar = (id) =>
    setVarSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const runVar = async (e) => {
    e.preventDefault();
    setRunningVar(true);
    setError("");
    try {
      const body = {
        entity_id: entityId,
        scenarios: varSel,
        transition_exposure: transitionExposure(varForm),
      };
      const r = await apiFetch(`/api/climate/scenarios/var`, { method: "POST", body });
      setVarResult(r);
    } catch (err) {
      setError(err.message || "VaR calculation failed");
    } finally {
      setRunningVar(false);
    }
  };

  // ── Carbon-price sensitivity form ────────────────────────────────────────────
  const [sensForm, setSensForm] = useState({
    scope1_tco2e: "", scope2_tco2e: "", scope3_tco2e: "",
  });
  const setSF = (k, v) => setSensForm((f) => ({ ...f, [k]: v }));
  const [runningSens, setRunningSens] = useState(false);

  const runSensitivity = async (e) => {
    e.preventDefault();
    setRunningSens(true);
    setError("");
    try {
      const body = {
        scope1_tco2e: Number(sensForm.scope1_tco2e) || 0,
        scope2_tco2e: Number(sensForm.scope2_tco2e) || 0,
        scope3_tco2e: Number(sensForm.scope3_tco2e) || 0,
      };
      const r = await apiFetch(`/api/climate/scenarios/carbon-price-sensitivity`, {
        method: "POST", body,
      });
      setSensResult(r);
    } catch (err) {
      setError(err.message || "Sensitivity calculation failed");
    } finally {
      setRunningSens(false);
    }
  };

  // ── Derived: max loss across the run for severity scaling ────────────────────
  const runByScenario = useMemo(() => {
    const phys = runResult?.physical_risk_assessment?.by_scenario || {};
    const trans = runResult?.transition_risk_assessment?.by_scenario || {};
    const ids = runResult?.scenarios_analyzed || [];
    return ids.map((id) => {
      const p = phys[id]?.total_risk_usd ?? 0;
      const t = trans[id]?.total_transition_risk ?? 0;
      return {
        id,
        scenario_name: phys[id]?.scenario_name || trans[id]?.scenario_name || id,
        physical: p,
        transition: t,
        total: p + t,
        risk_pct: phys[id]?.risk_as_percent_of_assets,
        temp: phys[id]?.global_temp_increase,
      };
    });
  }, [runResult]);
  const runMaxLoss = useMemo(
    () => runByScenario.reduce((m, r) => Math.max(m, r.total), 0),
    [runByScenario],
  );

  const varMaxLoss = useMemo(
    () => (varResult?.scenario_losses || []).reduce((m, r) => Math.max(m, r.loss_usd), 0),
    [varResult],
  );

  const tabs = [
    { id: "assets", label: "Assets", count: assets.length },
    { id: "run", label: "Run Analysis" },
    { id: "var", label: "Climate VaR" },
    { id: "sensitivity", label: "Carbon Price Sensitivity" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Page>
      <PageHeader
        overline="Climate Intelligence · NGFS Phase 4"
        OverlineIcon={Thermometer}
        title="Climate Scenarios & VaR"
        subtitle="Scenario analysis of physical + transition risk over registered assets (ESRS E1 / ISSB / TCFD quantitative disclosure)."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {/* Scenario reference strip (live NGFS catalog) */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <SectionTitle Icon={Thermometer}>NGFS Scenario Reference</SectionTitle>
          {provenance && (
            <DataQualityBadge
              authoritative={provenance.carbon_prices_authoritative}
              label={provenance.carbon_prices_authoritative
                ? "NGFS-traceable" : "Illustrative · not NGFS-traceable"}
              note={`Scenario set: ${provenance.vintage_used}. `
                  + `Current vintage: ${provenance.current_vintage}.`}
            />
          )}
        </div>
        {loading && scenarios.length === 0 ? (
          <div style={grid(190)}>
            <Skeleton h={90} /><Skeleton h={90} /><Skeleton h={90} />
          </div>
        ) : scenarios.length === 0 ? (
          <EmptyState title="No scenarios" body="The NGFS scenario catalog is unavailable." />
        ) : (
          <div style={grid(190)}>
            {scenarios.map((s) => {
              const { color, Icon, family } = scenarioTone(s.id);
              return (
                <div
                  key={s.id}
                  style={{
                    background: C.bg, border: `1px solid ${color}44`,
                    borderRadius: 8, padding: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontFamily: HEAD, fontSize: 13, fontWeight: 700, color: C.ink }}>
                      {s.name}
                    </span>
                    <Badge c={color} Icon={Icon} label={family} />
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <Data size={11} color={C.muted}>{s.id}</Data>
                  </div>
                  <div style={{ fontFamily: SANS, fontSize: 12, color: C.body, lineHeight: 1.45, marginBottom: 8 }}>
                    {s.description}
                  </div>
                  <div style={{ display: "flex", gap: 14 }}>
                    <div>
                      <div style={{ fontFamily: SANS, fontSize: 10, color: C.muted }}>2100 warming</div>
                      <Data size={12} color={color}>{fmt(s.global_temp_2100, 1)}°C</Data>
                    </div>
                    <div>
                      <div style={{ fontFamily: SANS, fontSize: 10, color: C.muted }}>C-price 2030</div>
                      <Data size={12} color={C.ink}>${fmt(s.carbon_price_2030, 0)}</Data>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {!entityId ? (
        <Card>
          <EmptyState
            Icon={Building2}
            title="Select a reporting entity"
            body="Choose an entity above to register climate-exposed assets and run NGFS scenario analysis."
          />
        </Card>
      ) : (
        <>
          <TabBar tabs={tabs} active={tab} onChange={setTab} />

          {/* ── Assets ─────────────────────────────────────────────────────── */}
          {tab === "assets" && (
            <Card>
              <SectionTitle
                Icon={Building2}
                right={
                  <Btn
                    variant="secondary"
                    Icon={showAssetForm ? ChevronDown : Plus}
                    onClick={() => setShowAssetForm((v) => !v)}
                  >
                    {showAssetForm ? "Hide form" : "Register asset"}
                  </Btn>
                }
              >
                Registered Assets
              </SectionTitle>

              {showAssetForm && (
                <form
                  onSubmit={submitAsset}
                  style={{
                    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: 16, marginBottom: 16,
                  }}
                >
                  <div style={grid(200)}>
                    <Field label="Asset name *">
                      <Input required value={assetForm.asset_name}
                        onChange={(e) => setAF("asset_name", e.target.value)} />
                    </Field>
                    <Field label="Asset type">
                      <Select value={assetForm.asset_type}
                        onChange={(e) => setAF("asset_type", e.target.value)}>
                        {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </Field>
                    <Field label="Location">
                      <Input value={assetForm.location}
                        onChange={(e) => setAF("location", e.target.value)} />
                    </Field>
                    <Field label="Country (ISO2) *">
                      <Input required maxLength={2} value={assetForm.country}
                        placeholder="DE"
                        onChange={(e) => setAF("country", e.target.value.toUpperCase())} />
                    </Field>
                    <Field label="Latitude *">
                      <Input required type="number" step="any" min={-90} max={90}
                        value={assetForm.latitude}
                        onChange={(e) => setAF("latitude", e.target.value)} />
                    </Field>
                    <Field label="Longitude *">
                      <Input required type="number" step="any" min={-180} max={180}
                        value={assetForm.longitude}
                        onChange={(e) => setAF("longitude", e.target.value)} />
                    </Field>
                    <Field label="Value (USD) *">
                      <Input required type="number" step="any" min={0}
                        value={assetForm.value_usd}
                        onChange={(e) => setAF("value_usd", e.target.value)} />
                    </Field>
                    <Field label="Useful life (years)">
                      <Input type="number" min={1} value={assetForm.useful_life_years}
                        onChange={(e) => setAF("useful_life_years", e.target.value)} />
                    </Field>
                    <Field label="Coastal proximity (km)">
                      <Input type="number" step="any" min={0}
                        value={assetForm.coastal_proximity_km}
                        onChange={(e) => setAF("coastal_proximity_km", e.target.value)} />
                    </Field>
                    <Field label="Flood zone">
                      <Select value={assetForm.flood_zone}
                        onChange={(e) => setAF("flood_zone", e.target.value)}>
                        {FLOOD_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                      </Select>
                    </Field>
                    <Field label="Water basin stress">
                      <Select value={assetForm.water_basin_stress}
                        onChange={(e) => setAF("water_basin_stress", e.target.value)}>
                        {WATER_STRESS.map((z) => <option key={z} value={z}>{z}</option>)}
                      </Select>
                    </Field>
                  </div>
                  <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                    <Btn type="submit" Icon={Plus} disabled={savingAsset}>
                      {savingAsset ? "Registering…" : "Register asset"}
                    </Btn>
                    <Btn type="button" variant="ghost" onClick={() => setShowAssetForm(false)}>
                      Cancel
                    </Btn>
                  </div>
                </form>
              )}

              {loading && assets.length === 0 ? (
                <Skeleton h={140} />
              ) : (
                <DataTable
                  keyField="asset_id"
                  emptyLabel="No assets registered for this entity"
                  columns={[
                    { key: "asset_name", label: "Asset",
                      render: (r) => <Data color={C.ink}>{r.asset_name}</Data> },
                    { key: "asset_type", label: "Type",
                      render: (r) => <Data color={C.body}>{r.asset_type}</Data> },
                    { key: "country", label: "Country",
                      render: (r) => <Data color={C.body}>{r.country}</Data> },
                    { key: "value_usd", label: "Value", align: "right",
                      render: (r) => <Data color={C.ink}>{USD(r.value_usd)}</Data> },
                    { key: "flood_zone", label: "Flood zone",
                      render: (r) => (
                        <Badge c={zoneTone(r.flood_zone)} Icon={Waves}
                          label={r.flood_zone || "none"} />
                      ) },
                    { key: "water_basin_stress", label: "Water stress",
                      render: (r) => (
                        <Badge c={zoneTone(r.water_basin_stress)} Icon={AlertTriangle}
                          label={r.water_basin_stress || "—"} />
                      ) },
                  ]}
                  rows={assets}
                />
              )}
            </Card>
          )}

          {/* ── Run Analysis ────────────────────────────────────────────────── */}
          {tab === "run" && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <SectionTitle Icon={Play}>Run Scenario Analysis</SectionTitle>
                <form onSubmit={runAnalysis}>
                  <div style={{ marginBottom: 14 }}>
                    <Label>Scenarios</Label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {NGFS_IDS.map((id) => (
                        <ScenarioChip key={id} id={id}
                          selected={runSel.includes(id)} onToggle={toggleRun} />
                      ))}
                    </div>
                  </div>
                  <div style={grid(200)}>
                    <Field label="Reporting period">
                      <Input value={runForm.reporting_period}
                        onChange={(e) => setRF("reporting_period", e.target.value)} />
                    </Field>
                    <Field label="Scope 1 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={runForm.scope1_emissions}
                        onChange={(e) => setRF("scope1_emissions", e.target.value)} />
                    </Field>
                    <Field label="Scope 2 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={runForm.scope2_emissions}
                        onChange={(e) => setRF("scope2_emissions", e.target.value)} />
                    </Field>
                    <Field label="Scope 3 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={runForm.scope3_emissions}
                        onChange={(e) => setRF("scope3_emissions", e.target.value)} />
                    </Field>
                    <Field label="Fossil-fuel assets value (USD)">
                      <Input type="number" step="any" min={0} value={runForm.fossil_fuel_assets_value}
                        onChange={(e) => setRF("fossil_fuel_assets_value", e.target.value)} />
                    </Field>
                    <Field label="Sector">
                      <Input value={runForm.sector}
                        onChange={(e) => setRF("sector", e.target.value)} />
                    </Field>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <Btn type="submit" Icon={Play} disabled={running || runSel.length === 0}>
                      {running ? "Running…" : "Run analysis"}
                    </Btn>
                  </div>
                </form>
              </Card>

              {runResult && (
                <Card>
                  <SectionTitle Icon={Thermometer}>
                    Results · {runResult.reporting_period || "—"}
                  </SectionTitle>
                  <div style={{ ...grid(180), marginBottom: 16 }}>
                    <Metric label="Assets analysed" value={fmt(runResult.asset_count, 0)} />
                    <Metric label="Total asset value" value={USD(runResult.total_asset_value_usd)} />
                    <Metric label="Expected loss (VaR)"
                      value={USD(runResult.climate_var?.expected_loss_usd)} color={C.accent} />
                    <Metric label="VaR 99%"
                      value={USD(runResult.climate_var?.var_99_usd)} color={C.destructive} />
                  </div>
                  <DataTable
                    keyField="id"
                    emptyLabel="No scenarios analysed"
                    columns={[
                      { key: "scenario_name", label: "Scenario",
                        render: (r) => (
                          <div>
                            <Data color={C.ink}>{r.scenario_name}</Data>
                            <div><Data size={10} color={C.muted}>{r.id}</Data></div>
                          </div>
                        ) },
                      { key: "temp", label: "2100", align: "right",
                        render: (r) => <Data color={C.body}>{fmt(r.temp, 1)}°C</Data> },
                      { key: "physical", label: "Physical", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.physical, runMaxLoss)}>{USD(r.physical)}</Data>
                        ) },
                      { key: "transition", label: "Transition", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.transition, runMaxLoss)}>{USD(r.transition)}</Data>
                        ) },
                      { key: "total", label: "Total loss", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.total, runMaxLoss)}>{USD(r.total)}</Data>
                        ) },
                      { key: "risk_pct", label: "% of assets", align: "right",
                        render: (r) => <Data color={C.body}>{fmt(r.risk_pct, 2)}%</Data> },
                    ]}
                    rows={runByScenario}
                  />
                </Card>
              )}
            </>
          )}

          {/* ── Climate VaR ─────────────────────────────────────────────────── */}
          {tab === "var" && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <SectionTitle Icon={ShieldAlert}>Climate Value-at-Risk</SectionTitle>
                <form onSubmit={runVar}>
                  <div style={{ marginBottom: 14 }}>
                    <Label>Scenarios (loss distribution)</Label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {NGFS_IDS.map((id) => (
                        <ScenarioChip key={id} id={id}
                          selected={varSel.includes(id)} onToggle={toggleVar} />
                      ))}
                    </div>
                  </div>
                  <div style={grid(200)}>
                    <Field label="Scope 1 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={varForm.scope1_emissions}
                        onChange={(e) => setVF("scope1_emissions", e.target.value)} />
                    </Field>
                    <Field label="Scope 2 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={varForm.scope2_emissions}
                        onChange={(e) => setVF("scope2_emissions", e.target.value)} />
                    </Field>
                    <Field label="Scope 3 emissions (tCO₂e)">
                      <Input type="number" step="any" min={0} value={varForm.scope3_emissions}
                        onChange={(e) => setVF("scope3_emissions", e.target.value)} />
                    </Field>
                    <Field label="Fossil-fuel assets value (USD)">
                      <Input type="number" step="any" min={0} value={varForm.fossil_fuel_assets_value}
                        onChange={(e) => setVF("fossil_fuel_assets_value", e.target.value)} />
                    </Field>
                    <Field label="Sector">
                      <Input value={varForm.sector}
                        onChange={(e) => setVF("sector", e.target.value)} />
                    </Field>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <Btn type="submit" Icon={ShieldAlert} disabled={runningVar || varSel.length === 0}>
                      {runningVar ? "Calculating…" : "Compute VaR"}
                    </Btn>
                  </div>
                </form>
              </Card>

              {varResult && (
                <Card>
                  <SectionTitle Icon={ShieldAlert}>
                    VaR · horizon {varResult.time_horizon || "—"} · {varResult.weighting || "equal"} weights
                  </SectionTitle>
                  <div style={{ ...grid(160), marginBottom: 16 }}>
                    <Metric label="Expected loss" value={USD(varResult.expected_loss_usd)} color={C.body} />
                    <Metric label="VaR 95%" value={USD(varResult.var_95_usd)} color={C.accent} />
                    <Metric label="VaR 99%" value={USD(varResult.var_99_usd)} color={C.destructive} />
                    <Metric label="ES 95%" value={USD(varResult.expected_shortfall_95_usd)} color={C.accent} />
                    <Metric label="ES 99%" value={USD(varResult.expected_shortfall_99_usd)} color={C.destructive} />
                  </div>
                  <DataTable
                    keyField="scenario"
                    emptyLabel="No scenario losses"
                    columns={[
                      { key: "scenario_name", label: "Scenario",
                        render: (r) => (
                          <div>
                            <Data color={C.ink}>{r.scenario_name}</Data>
                            <div><Data size={10} color={C.muted}>{r.scenario}</Data></div>
                          </div>
                        ) },
                      { key: "physical_loss_usd", label: "Physical", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.physical_loss_usd, varMaxLoss)}>
                            {USD(r.physical_loss_usd)}
                          </Data>
                        ) },
                      { key: "transition_loss_usd", label: "Transition", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.transition_loss_usd, varMaxLoss)}>
                            {USD(r.transition_loss_usd)}
                          </Data>
                        ) },
                      { key: "loss_usd", label: "Total loss", align: "right",
                        render: (r) => (
                          <Data color={severityColor(r.loss_usd, varMaxLoss)}>{USD(r.loss_usd)}</Data>
                        ) },
                      { key: "weight", label: "Weight", align: "right",
                        render: (r) => <Data color={C.body}>{fmt(r.weight, 4)}</Data> },
                    ]}
                    rows={varResult.scenario_losses || []}
                  />
                  {varResult.methodology && (
                    <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginTop: 12, lineHeight: 1.5 }}>
                      {varResult.methodology}
                    </p>
                  )}
                </Card>
              )}
            </>
          )}

          {/* ── Carbon Price Sensitivity ────────────────────────────────────── */}
          {tab === "sensitivity" && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <SectionTitle Icon={Coins}>Carbon Price Sensitivity</SectionTitle>
                <form onSubmit={runSensitivity}>
                  <div style={grid(200)}>
                    <Field label="Scope 1 (tCO₂e)">
                      <Input type="number" step="any" min={0} value={sensForm.scope1_tco2e}
                        onChange={(e) => setSF("scope1_tco2e", e.target.value)} />
                    </Field>
                    <Field label="Scope 2 (tCO₂e)">
                      <Input type="number" step="any" min={0} value={sensForm.scope2_tco2e}
                        onChange={(e) => setSF("scope2_tco2e", e.target.value)} />
                    </Field>
                    <Field label="Scope 3 (tCO₂e)">
                      <Input type="number" step="any" min={0} value={sensForm.scope3_tco2e}
                        onChange={(e) => setSF("scope3_tco2e", e.target.value)} />
                    </Field>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <Btn type="submit" Icon={Coins} disabled={runningSens}>
                      {runningSens ? "Calculating…" : "Compute sensitivity"}
                    </Btn>
                  </div>
                </form>
              </Card>

              {sensResult && (
                <Card>
                  <SectionTitle Icon={Coins}>Price Ladder</SectionTitle>
                  <div style={{ ...grid(160), marginBottom: 16 }}>
                    <Metric label="Priced tCO₂e (1+2)"
                      value={fmt(sensResult.emissions_profile?.priced_tco2e, 0)} />
                    <Metric label="Scope 3 tCO₂e"
                      value={fmt(sensResult.emissions_profile?.scope3_tco2e, 0)} color={C.body} />
                  </div>
                  <DataTable
                    keyField="price_usd_per_tco2e"
                    emptyLabel="No price points"
                    columns={[
                      { key: "price_usd_per_tco2e", label: "Carbon price", align: "left",
                        render: (r) => (
                          <Data color={C.ink}>${fmt(r.price_usd_per_tco2e, 0)}/tCO₂e</Data>
                        ) },
                      { key: "annual_cost_usd", label: "Annual cost (1+2)", align: "right",
                        render: (r) => <Data color={C.accent}>{USD(r.annual_cost_usd)}</Data> },
                      { key: "annual_cost_incl_scope3_usd", label: "Incl. scope 3", align: "right",
                        render: (r) => (
                          <Data color={C.destructive}>{USD(r.annual_cost_incl_scope3_usd)}</Data>
                        ) },
                    ]}
                    rows={sensResult.price_ladder || []}
                  />
                  {sensResult.note && (
                    <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginTop: 12, lineHeight: 1.5 }}>
                      {sensResult.note}
                    </p>
                  )}
                </Card>
              )}
            </>
          )}
        </>
      )}
    </Page>
  );
}
